import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Row } from 'react-table';
import { FixedSizeList } from 'react-window';
import { VirtualizeOptions } from './DataGrid';
import { TableRowWrapper } from './TableRowWrapper';
interface FixedSizeListPartialState {
  scrollOffset: number;
}

type VirtualizedTableBodyProps<D extends Record<string, unknown>> = {
  data?: D[];
  rows: Row<D>[];
  virtualizeOptions?: VirtualizeOptions;
  prepareRow: (row: Row<D>) => void;
  onRowClick?: (row: Row<D>) => void;
  tableStateChangeListener: boolean;
  tableOptions?: { [key: string]: any }; // eslint-disable-line @typescript-eslint/no-explicit-any
  /** Callback use for FixedSizeList to inject business logic during scrolling */
  onScroll?: (args: {
    scrollDirection: 'forward' | 'backward';
    scrollOffset: number;
    scrollUpdateWasRequested: boolean;
  }) => void;
};

export function VirtualizedTableBody<T extends Record<string, unknown>>({
  data = [],
  rows,
  onRowClick = () => undefined,
  virtualizeOptions,
  prepareRow,
  tableStateChangeListener,
  tableOptions,
  onScroll,
}: VirtualizedTableBodyProps<T>) {
  const listRef = useRef<FixedSizeList>(null);

  const getUpdatedTableHeight = () => window.innerHeight - (virtualizeOptions?.tablePadding || 400);
  const [tableHeight, setTableHeight] = useState(getUpdatedTableHeight());
  const itemCount = data.length;
  const itemSize = virtualizeOptions?.rowHeight || 56;

  const updateHeightCallback = () => {
    setTableHeight(getUpdatedTableHeight());
  };

  useEffect(() => {
    setTableHeight(getUpdatedTableHeight());
    window.addEventListener('resize', updateHeightCallback);
    return () => {
      window.removeEventListener('resize', updateHeightCallback);
    };
  }, []);

  const handleKeyDown = (event: KeyboardEvent) => {
    const listRefScrollOffset = (listRef?.current?.state as FixedSizeListPartialState)?.scrollOffset || 0;
    const handleNewPosition = (offset: number, currentPosition = listRefScrollOffset) =>
      listRef?.current?.scrollTo(offset + currentPosition);
    if (event.target === document.body) {
      switch (event.key) {
        case 'PageUp':
          handleNewPosition(-tableHeight);
          break;
        case 'PageDown':
          handleNewPosition(tableHeight);
          break;
        case 'ArrowUp':
          handleNewPosition(-itemSize);
          break;
        case 'ArrowDown':
          handleNewPosition(itemSize);
          break;
        case 'Home':
          handleNewPosition(0, 0);
          break;
        case 'End':
          handleNewPosition(itemSize * itemCount - tableHeight);
          break;
      }
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [itemCount]);

  const RenderRow = useCallback(
    ({ data, index, style }) => {
      const row = data[index];
      prepareRow(row);
      return (
        <TableRowWrapper index={index} row={row} style={style} tableOptions={tableOptions} onRowClick={onRowClick} />
      );
    },
    [prepareRow, tableStateChangeListener],
  );

  return (
    <FixedSizeList
      style={{ overflowX: 'hidden', overflow: 'overlay' }}
      ref={listRef}
      height={tableHeight}
      itemCount={rows.length}
      itemSize={itemSize}
      itemData={rows}
      width={'100%'}
      onScroll={onScroll}
      data-testid="fixed-size-list"
    >
      {RenderRow}
    </FixedSizeList>
  );
}
